import { CommonModule } from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerModule, KENDO_DATEINPUT, KENDO_DATEPICKER } from '@progress/kendo-angular-dateinputs';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { FinCenService } from '../../@theme/services/fincen.service';

@Component({
  selector: 'app-fincen',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule,
    DatePickerModule,
    KENDO_DATEPICKER,
    KENDO_DATEINPUT
  ],
  templateUrl: './fincen.component.html',
  styleUrl: './fincen.component.scss'
})
export class FincenComponent implements OnInit, OnDestroy {
  @ViewChild('personFileInput') personFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('businessFileInput') businessFileInput!: ElementRef<HTMLInputElement>;
  
  uploadForm: FormGroup;
  personFile: File | null = null;
  businessFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  errorMessage = '';
  resultsMessage = '';
  minDate = new Date(1900, 0, 1);
  maxDate = new Date();

  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private fincenService: FinCenService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    this.uploadForm = this.fb.group({
      uploadDate: [null, Validators.required]
    });
  }

  onPersonFileSelect(event: Event): void {
    this.errorMessage = '';
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.validateFile(file)) {
        this.personFile = file;
      } else {
        this.personFile = null;
        input.value = ''; // Clear the input
      }
    }
  }

  onBusinessFileSelect(event: Event): void {
    this.errorMessage = '';
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.validateFile(file)) {
        this.businessFile = file;
      } else {
        this.businessFile = null;
        input.value = ''; // Clear the input
      }
    }
  }

  private validateFile(file: File): boolean {
    const allowedExtensions = ['.csv', '.txt', '.pdf'];
    const maxSize = 1024000000; // 1GB
    
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedExtensions.includes(extension)) {
      this.errorMessage = `Invalid file type. Only ${allowedExtensions.join(', ')} files are allowed.`;
      this.toastr.error(this.errorMessage, 'Validation Error');
      return false;
    }
    
    if (file.size > maxSize) {
      this.errorMessage = 'File size exceeds maximum limit of 1GB.';
      this.toastr.error(this.errorMessage, 'Validation Error');
      return false;
    }
    
    return true;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  uploadFiles(): void {
    if (this.uploadForm.invalid) {
      this.toastr.warning('Please select a date', 'Validation');
      return;
    }

    if (!this.personFile && !this.businessFile) {
      this.toastr.warning('Please select at least one file to upload', 'Validation');
      return;
    }

    const uploadDate = this.uploadForm.get('uploadDate')?.value;
    const formData = new FormData();
    
    // Add the date - format for C# DateTime parsing
    // Send in a format that C# can parse directly
    const dateStr = uploadDate.toLocaleDateString('en-US'); // MM/DD/YYYY format
    formData.append('uploadDate', dateStr);
    
    // Add files - C# expects IFormFile parameters, send empty blobs if not selected
    if (this.personFile) {
      formData.append('personFile', this.personFile, this.personFile.name);
    } else {
      // Send empty blob with no filename if no file selected
      formData.append('personFile', new Blob(), '');
    }
    
    if (this.businessFile) {
      formData.append('businessFile', this.businessFile, this.businessFile.name);
    } else {
      // Send empty blob with no filename if no file selected
      formData.append('businessFile', new Blob(), '');
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';
    this.resultsMessage = '';

    this.subscription.add(
      this.fincenService.uploadFinCenFiles(formData).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          } else if (event.type === HttpEventType.Response) {
            this.isUploading = false;
            this.uploadProgress = 100;
            this.resultsMessage = 'Files uploaded successfully!';
            this.toastr.success('FinCEN files uploaded successfully', 'Success');
            
            // Reset form and files
            this.personFile = null;
            this.businessFile = null;
            this.uploadForm.reset();
            
            // Clear file inputs
            if (this.personFileInput) {
              this.personFileInput.nativeElement.value = '';
            }
            if (this.businessFileInput) {
              this.businessFileInput.nativeElement.value = '';
            }
          }
        },
        error: (err) => {
          this.isUploading = false;
          this.uploadProgress = 0;
          const errorMsg = err?.error?.message || err?.message || 'Failed to upload files';
          this.errorMessage = errorMsg;
          this.toastr.error(errorMsg, 'Upload Error');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
